import {
	Bell,
	Check,
	UserRound,
	Heart,
	Menu,
	X,
	ClipboardList,
	User,
	LogOut,
	LogIn,
	UserPlus,
	RefreshCw,
	CheckCheck,
	ChevronRight,
	Clock3,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { logout } from "../core/auth/auth";
import { ModalCerrarSesion } from "./modals/ModalCerrarSesion";
import { usePaymentExchangeRate } from "../hooks/services";
import type { NavBarProps } from "../core/types/tsx/components/NavBar.types";
import { useNotifications } from "../state/NotificationsContext";
import { useAuthSessionContext } from "./routing/auth-session-store";

const EXCHANGE_RATE_FALLBACK = 36;
const EXCHANGE_RATE_CACHE_KEY = "servix_usd_to_bs_rate";

const readCachedExchangeRate = (): number | null => {
	try {
		const raw = window.localStorage.getItem(EXCHANGE_RATE_CACHE_KEY);
		if (!raw) return null;
		const parsed = Number(raw);
		return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
	} catch {
		return null;
	}
};

export const NavBar = ({ isLogged = false }: NavBarProps) => {
	const navigate = useNavigate();
	const { session } = useAuthSessionContext();
	const isClientSession = session?.user?.type_user === "Cliente";
	const isProviderSession = session?.user?.type_user === "Proveedor";
	const canSeeProfileShortcut = isClientSession || isProviderSession;
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [isAuthMenuOpen, setIsAuthMenuOpen] = useState(false);
	const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
	const [showLogoutModal, setShowLogoutModal] = useState(false);

	const {
		data: exchangeRateData,
		loading: exchangeRateLoading,
		error: exchangeRateError,
	} = usePaymentExchangeRate();

	const { notifications, unreadCount, markAsRead, markAllAsRead, refresh } =
		useNotifications();

	const [cachedExchangeRate, setCachedExchangeRate] = useState<number | null>(() =>
		readCachedExchangeRate(),
	);
	const profileMenuRef = useRef<HTMLDivElement | null>(null);
	const authMenuRef = useRef<HTMLDivElement | null>(null);
	const notificationsMenuRef = useRef<HTMLDivElement | null>(null);
	const mobileMenuRef = useRef<HTMLDivElement | null>(null);
	const mobileMenuPanelRef = useRef<HTMLDivElement | null>(null);

	const apiExchangeRateValue =
		typeof exchangeRateData?.usd_to_bs === "number" &&
		Number.isFinite(exchangeRateData.usd_to_bs) &&
		exchangeRateData.usd_to_bs > 0
			? exchangeRateData.usd_to_bs
			: null;

	useEffect(() => {
		if (!apiExchangeRateValue) return;
		setCachedExchangeRate(apiExchangeRateValue);
		try {
			window.localStorage.setItem(EXCHANGE_RATE_CACHE_KEY, String(apiExchangeRateValue));
		} catch {
			// Ignore localStorage failures (private mode/quota)
		}
	}, [apiExchangeRateValue]);

	const displayExchangeRate = apiExchangeRateValue ?? cachedExchangeRate ?? EXCHANGE_RATE_FALLBACK;
	const exchangeRateLabel = Number.isFinite(displayExchangeRate)
		? `1$ = ${new Intl.NumberFormat("es-VE", {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			}).format(displayExchangeRate)}Bs`
		: exchangeRateError
			? "BCV N/D"
			: exchangeRateLoading
				? "Cargando tasa..."
				: "BCV N/D";

	useEffect(() => {
		if (!isMenuOpen && !isAuthMenuOpen && !isNotificationsOpen && !isMobileMenuOpen)
			return;
		const onPointerDown = (e: MouseEvent) => {
			if (!(e.target instanceof Node)) return;
			if (profileMenuRef.current?.contains(e.target)) return;
			if (authMenuRef.current?.contains(e.target)) return;
			if (notificationsMenuRef.current?.contains(e.target)) return;
			if (mobileMenuRef.current?.contains(e.target)) return;
			if (mobileMenuPanelRef.current?.contains(e.target)) return;
			setIsMenuOpen(false);
			setIsAuthMenuOpen(false);
			setIsNotificationsOpen(false);
			setIsMobileMenuOpen(false);
		};
		window.addEventListener("mousedown", onPointerDown);
		return () => window.removeEventListener("mousedown", onPointerDown);
	}, [isMenuOpen, isAuthMenuOpen, isNotificationsOpen, isMobileMenuOpen]);

	const onLogout = () => {
		setIsMenuOpen(false);
		setIsMobileMenuOpen(false);
		setShowLogoutModal(true);
	};

	const confirmLogout = () => {
		void logout();
		setShowLogoutModal(false);
		navigate("/");
	};

	const closeAuthMenu = () => setIsAuthMenuOpen(false);

	const goToClientOrders = () => {
		setIsMobileMenuOpen(false);
		navigate("/user-profile", {
			state: { initialTab: "orders" },
		});
	};

	const goToProviderOrders = () => {
		setIsMobileMenuOpen(false);
		navigate("/service/profile", {
			state: { initialTab: "pedidos" },
		});
	};

	const goToClientFavorites = () => {
		setIsMobileMenuOpen(false);
		navigate("/user-profile", {
			state: { initialTab: "favoritos" },
		});
	};

	const toggleNotifications = () => {
		setIsNotificationsOpen((prev) => !prev);
	};

	const mobileItemClassName =
		"w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-left text-sm font-medium text-gray-700 transition-colors hover:border-primary/30 hover:bg-primary/5";

	return (
		<>
			<ModalCerrarSesion
				open={showLogoutModal}
				onClose={() => setShowLogoutModal(false)}
				onCancel={() => setShowLogoutModal(false)}
				onConfirm={confirmLogout}
			/>
			<nav className='bg-[#FCFCFC] py-4 relative z-10 font-[Inter] border-b border-gray-200'>
				<div className='flex justify-between items-center px-4 sm:px-6 lg:px-16'>
					<div className=''>
						<a href='/' className='cursor-pointer  text-xl font-bold'>
							<img
								src='/isologo.svg'
								className='w-24 h-12 ml-2'
								alt='Logo de Servix'
							/>
						</a>
					</div>

					<ul className='hidden lg:flex items-center gap-4'>
						{
							<>
								{isLogged && (isClientSession || isProviderSession) ? (
									<li>
										<button
											type='button'
											onClick={
												isProviderSession
													? goToProviderOrders
													: goToClientOrders
											}
											className='text-sm font-medium text-gray-700 hover:text-primary transition-colors cursor-pointer'
										>
											Ordenes
										</button>
									</li>
								) : null}
								{isLogged && isClientSession ? (
									<li>
										<button
											type='button'
											onClick={goToClientFavorites}
											className='cursor-pointer bg-primary hover:bg-primary-hover text-white w-8 h-8 rounded-full flex items-center justify-center transition-colors'
											title='Favoritos'
										>
											<Heart size={17} />
										</button>
									</li>
								) : null}
								<li>
									{isLogged ? (
										<div
											className='relative'
											ref={notificationsMenuRef}
										>
											<button
												type='button'
												onClick={toggleNotifications}
												className='cursor-pointer relative bg-primary hover:bg-primary-hover text-white w-8 h-8 rounded-full flex items-center justify-center transition-colors'
												title='Notificaciones'
											>
												<Bell size={17} />
												{unreadCount > 0 ? (
													<span className='absolute -bottom-1 -right-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full border-2 border-white bg-orange-500 px-1 text-[9px] font-semibold text-white'>
														{unreadCount > 9
															? "9+"
															: unreadCount}
													</span>
												) : null}
											</button>

											{isNotificationsOpen ? (
												<div className='absolute right-0 mt-2 w-80 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg'>
													<div className='flex items-center justify-between border-b border-gray-100 px-3 py-2'>
														<span className='text font-semibold text-gray-800'>
															Notificaciones
														</span>
														<div className='flex items-center gap-2'>
															<button
																type='button'
																onClick={() => {
																	void refresh();
																}}
																className='cursor-pointer text-[11px] font-medium text-primary hover:underline'
															>
																Actualizar
															</button>
															<button
																type='button'
																onClick={markAllAsRead}
																className='cursor-pointer text-[11px] font-medium text-primary hover:underline'
															>
																Marcar todas
															</button>
														</div>
													</div>
													<div className='max-h-80 overflow-y-auto'>
														{notifications.length === 0 ? (
															<p className='px-3 py-4 text-sm text-gray-500'>
																Sin notificaciones.
															</p>
														) : (
															notifications.map(
																(notification) => (
																	<div
																		key={
																			notification.id
																		}
																		className={`w-full cursor-pointer border-b border-gray-200 px-3 py-2 text-left transition hover:bg-gray-50 ${
																			notification.isRead
																				? "bg-white"
																				: "bg-blue-50/40"
																		}`}
																	>
																		<div className='flex items-center justify-between gap-2'>
																			<p className='text-sm font-semibold text-gray-800'>
																				{
																					notification.title
																				}
																			</p>
																			<div className='flex items-center gap-2'>
																				{!notification.isRead ? (
																					<span className='h-2 w-2 rounded-full bg-primary' />
																				) : null}
																				<button
																					type='button'
																					onClick={(
																						event,
																					) => {
																						event.stopPropagation();
																						markAsRead(
																							notification.id,
																						);
																					}}
																					className='cursor-pointer rounded-full p-1 text-primary transition-colors hover:bg-primary/10'
																					title='Marcar como leída'
																					aria-label='Marcar como leída'
																				>
																					<Check
																						size={
																							14
																						}
																					/>
																				</button>
																			</div>
																		</div>
																		<p className='mt-0.5 text-xs text-gray-600'>
																			{
																				notification.message
																			}
																		</p>
																		<p className='mt-1 text-[11px] text-gray-400'>
																			{
																				notification.createdAt
																			}
																		</p>
																	</div>
																),
															)
														)}
													</div>
												</div>
											) : null}
										</div>
									) : null}
								</li>
								<li>
									{isLogged ? (
										<div className='relative' ref={profileMenuRef}>
											<button
												type='button'
												onClick={() => setIsMenuOpen((v) => !v)}
												className='cursor-pointer bg-primary hover:bg-primary-hover text-white w-8 h-8 rounded-full flex items-center justify-center transition-colors'
												title='Menú de usuario'
											>
												<UserRound size={20} />
											</button>

											{isMenuOpen && (
												<div className='absolute right-0 mt-2 w-44 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg'>
													{canSeeProfileShortcut ? (
														<Link
															to='/profile'
															className='block w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50'
															onClick={() =>
																setIsMenuOpen(false)
															}
														>
															Ver perfil
														</Link>
													) : null}
													<button
														type='button'
														className='cursor-pointer block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50'
														onClick={onLogout}
													>
														Cerrar sesión
													</button>
												</div>
											)}
										</div>
									) : null}
								</li>
								<li>
									{!isLogged ? (
										<div className='relative' ref={authMenuRef}>
											<button
												type='button'
												onClick={() =>
													setIsAuthMenuOpen((prev) => !prev)
												}
												className='font-normal cursor-pointer text-sm bg-primary hover:bg-primary-hover text-white px-4 py-2 rounded-full hover:bg-primary-hover-hover transition-colors'
											>
												Inicio/Registro
											</button>

											{isAuthMenuOpen ? (
												<div className='absolute right-0 mt-2 w-44 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg'>
													<Link
														to='/login'
														className='block w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50'
														onClick={closeAuthMenu}
													>
														Iniciar sesión
													</Link>
													<Link
														to='/registro'
														className='block w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50'
														onClick={closeAuthMenu}
													>
														Registrarme
													</Link>
												</div>
											) : null}
										</div>
									) : null}
								</li>
								<li>
									<button className='font-normal text-sm bg-secondary text-primary hover:bg-secondary-hover hover:text-primary-hover/75 px-4 py-2 rounded-full flex items-center gap-1 transition-colors'>
										{/* <MapPin size={16} /> */}
										{exchangeRateLabel}
									</button>
								</li>
							</>
						}
					</ul>

					<div
						className='flex items-center gap-2 lg:hidden'
						ref={mobileMenuRef}
					>
						<button
							type='button'
							className='max-w-[42vw] truncate font-normal text-xs bg-secondary text-primary hover:bg-secondary-hover hover:text-primary-hover/75 px-3 py-2 rounded-full flex items-center gap-1 transition-colors'
							title={exchangeRateLabel}
						>
							{exchangeRateLabel}
						</button>

						<button
							type='button'
							onClick={() => setIsMobileMenuOpen((prev) => !prev)}
							className='cursor-pointer bg-primary hover:bg-primary-hover text-white w-9 h-9 rounded-full flex items-center justify-center transition-colors'
							aria-label='Abrir menú'
						>
							{isMobileMenuOpen ? <X size={18} /> : <Menu size={18} />}
						</button>
					</div>
				</div>

				{isMobileMenuOpen ? (
					<div
						className='lg:hidden border-t border-gray-200 mt-3 px-4 sm:px-6 pb-4'
						ref={mobileMenuPanelRef}
					>
						<div className='pt-3 space-y-2'>
							{isLogged && (isClientSession || isProviderSession) ? (
								<button
									type='button'
									onClick={
										isProviderSession
											? goToProviderOrders
											: goToClientOrders
									}
									className={mobileItemClassName}
								>
									<span className='flex items-center justify-between gap-2'>
										<span className='flex items-center gap-2'>
											<ClipboardList
												size={16}
												className='text-primary'
											/>
											Ordenes
										</span>
										<ChevronRight
											size={16}
											className='text-gray-400'
										/>
									</span>
								</button>
							) : null}

							{isLogged && isClientSession ? (
								<button
									type='button'
									onClick={goToClientFavorites}
									className={mobileItemClassName}
								>
									<span className='flex items-center justify-between gap-2'>
										<span className='flex items-center gap-2'>
											<Heart size={16} className='text-primary' />
											Favoritos
										</span>
										<ChevronRight
											size={16}
											className='text-gray-400'
										/>
									</span>
								</button>
							) : null}

							{isLogged ? (
								<div className='relative' ref={notificationsMenuRef}>
									<button
										type='button'
										onClick={toggleNotifications}
										className={`${mobileItemClassName} ${
											isNotificationsOpen
												? "border-primary/40 bg-primary/5"
												: ""
										} flex items-center justify-between gap-2`}
									>
										<span className='flex items-center gap-2'>
											<Bell size={16} className='text-primary' />
											Notificaciones
										</span>
										<span className='flex items-center gap-2'>
											{unreadCount > 0 ? (
												<span className='inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-orange-500 px-1 text-[10px] font-semibold text-white'>
													{unreadCount > 9 ? "9+" : unreadCount}
												</span>
											) : null}
											<ChevronRight
												size={16}
												className={`text-gray-400 transition-transform ${
													isNotificationsOpen ? "rotate-90" : ""
												}`}
											/>
										</span>
									</button>

									{isNotificationsOpen ? (
										<div className='mt-2 overflow-hidden rounded-2xl border border-primary/20 bg-linear-to-b from-blue-50/80 to-white shadow-sm'>
											<div className='flex items-center justify-between border-b border-primary/10 px-3 py-2.5'>
												<div className='flex items-center gap-2'>
													<div className='rounded-full bg-primary/10 p-1.5 text-primary'>
														<Bell size={14} />
													</div>
													<span className='text-sm font-semibold text-gray-800'>
														Notificaciones
													</span>
												</div>
												<div className='flex items-center gap-2'>
													<button
														type='button'
														onClick={() => {
															void refresh();
														}}
														className='cursor-pointer rounded-full border border-gray-200 bg-white p-1.5 text-primary transition-colors hover:bg-primary/10'
														title='Actualizar'
														aria-label='Actualizar notificaciones'
													>
														<RefreshCw size={13} />
													</button>
													<button
														type='button'
														onClick={markAllAsRead}
														className='cursor-pointer rounded-full border border-gray-200 bg-white p-1.5 text-primary transition-colors hover:bg-primary/10'
														title='Marcar todas como leídas'
														aria-label='Marcar todas como leídas'
													>
														<CheckCheck size={13} />
													</button>
												</div>
											</div>
											<div className='max-h-80 overflow-y-auto p-2'>
												{notifications.length === 0 ? (
													<p className='px-3 py-4 text-center text-sm text-gray-500'>
														Sin notificaciones.
													</p>
												) : (
													notifications.map((notification) => (
														<article
															key={notification.id}
															className={`mb-2 rounded-xl border px-3 py-2.5 text-left transition ${
																notification.isRead
																	? "bg-white"
																	: "bg-blue-50/40"
															} ${
																notification.isRead
																	? "border-gray-200"
																	: "border-primary/20"
															}`}
														>
															<div className='flex items-center justify-between gap-2'>
																<p className='text-sm font-semibold leading-snug text-gray-800'>
																	{notification.title}
																</p>
																<div className='flex items-center gap-2'>
																	{!notification.isRead ? (
																		<span className='h-2 w-2 rounded-full bg-primary' />
																	) : null}
																	<button
																		type='button'
																		onClick={(
																			event,
																		) => {
																			event.stopPropagation();
																			markAsRead(
																				notification.id,
																			);
																		}}
																		className='cursor-pointer rounded-full p-1 text-primary transition-colors hover:bg-primary/10'
																		title='Marcar como leída'
																		aria-label='Marcar como leída'
																	>
																		<Check
																			size={14}
																		/>
																	</button>
																</div>
															</div>
															<p className='mt-0.5 text-xs text-gray-600'>
																{notification.message}
															</p>
															<div className='mt-1 flex items-center gap-1 text-[11px] text-gray-400'>
																<Clock3 size={11} />
																<span>
																	{
																		notification.createdAt
																	}
																</span>
															</div>
														</article>
													))
												)}
											</div>
										</div>
									) : null}
								</div>
							) : null}

							{isLogged && canSeeProfileShortcut ? (
								<Link
									to='/profile'
									onClick={() => setIsMobileMenuOpen(false)}
									className={`${mobileItemClassName} block`}
								>
									<span className='flex items-center justify-between gap-2'>
										<span className='flex items-center gap-2'>
											<User size={16} className='text-primary' />
											Ver perfil
										</span>
										<ChevronRight
											size={16}
											className='text-gray-400'
										/>
									</span>
								</Link>
							) : null}

							{isLogged ? (
								<button
									type='button'
									onClick={onLogout}
									className={`${mobileItemClassName} border-red-100 text-red-600 hover:bg-red-50 hover:border-red-200`}
								>
									<span className='flex items-center justify-between gap-2'>
										<span className='flex items-center gap-2'>
											<LogOut size={16} />
											Cerrar sesión
										</span>
										<ChevronRight
											size={16}
											className='text-red-300'
										/>
									</span>
								</button>
							) : (
								<div className='space-y-2 pt-1'>
									<Link
										to='/login'
										onClick={() => setIsMobileMenuOpen(false)}
										className='block w-full rounded-full bg-primary px-4 py-2.5 text-center text-sm font-medium text-white transition-colors hover:bg-primary-hover'
									>
										<span className='inline-flex items-center gap-2'>
											<LogIn size={16} />
											Iniciar sesión
										</span>
									</Link>
									<Link
										to='/registro'
										onClick={() => setIsMobileMenuOpen(false)}
										className='block w-full rounded-full border border-primary bg-white px-4 py-2.5 text-center text-sm font-medium text-primary transition-colors hover:bg-primary/5'
									>
										<span className='inline-flex items-center gap-2'>
											<UserPlus size={16} />
											Registrarme
										</span>
									</Link>
								</div>
							)}
						</div>
					</div>
				) : null}
			</nav>
		</>
	);
};
